/**
 * The full column pass, phase 1 (`ColumnBindPasses.full`): reconcile the
 * column's cells against the target rows by stable row identity, and compute
 * each row's plan and cell binding once, before any structure is applied.
 * Moved verbatim out of `fullPass` (r5 FS 1); what each step does and why is
 * in the comments it carries.
 */
import { RenderPlan } from "@tento-lona/sheets/platform";
import type { Option } from "@tento-lona/sheets";
import { Changeset } from "@tento-core/changeset";
import { Tracing } from "@tento-core/tracing";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { reconcileRowKeys } from "./row-reconcile-keys.ts";
import { assembleCommitOps, summarizeCommitOps } from "./commit-op-list.ts";
import { type CellBinding, DefaultBinderHelpers } from "./default-binder-helpers.ts";
import type { ColumnBindPort, ColumnPassInput } from "./column-bind-port.ts";

/** What phase 1 resolved, read by every later phase of the same pass. */
export type FullPassReconciled = {
  /** Each old cell's content type, by its ORIGINAL (unspliced) position. */
  readonly oldContentTypes: readonly Option<string>[];
  /** Each old cell's row key, a promote re-key mapped back to the row it still paints. */
  readonly oldRowIds: ReturnType<typeof reconcileRowKeys>;
  /** Each target row's key (`row.id.toString()`), in target order. */
  readonly targetRowIds: string[];
  /** The positional changes from the old keys to the target keys. */
  readonly changesets: ReturnType<typeof Changeset.make>;
  /** The target rows by key. */
  readonly rowByKey: ReadonlyMap<string, ColumnPassInput["rows"][number]>;
  /** Each target row's plan, computed before any structure applies. */
  readonly plans: RenderPlan<ColumnIndex.Key.Resolved.Default>[];
  /** Each target row's cell binding, resolved once (`null`: not cell-bindable this pass). */
  readonly bindings: Option<CellBinding>[];
};

/** Phase 1 of the full pass: the reconcile keys, plans and bindings. */
export function reconcileFullPass(port: ColumnBindPort, input: ColumnPassInput): FullPassReconciled {
  const { sheet, defaultColumn, $cells, rows, widthPx, now } = input;
  // Reconcile rendered cells against the target rows by STABLE ROW IDENTITY
  // (`row.id.toString()` — the canonical RowKey wire-string, deterministic
  // across rebuilds), NOT by contentType+position. Keying by contentType made
  // two same-type rows indistinguishable, so removing one row could "keep" a
  // surviving cell while silently reassigning it to a different row; span-mode
  // `skipContent` then skipped the content rebind, leaving a zombie row (right
  // count, stale content). Identity-keying deletes the exact vanished cell and
  // reuses each surviving cell for the same row. A `null` rid (a fresh,
  // never-bound cell) never matches a target id, so it correctly diffs as
  // insert/replace → full rebind (where `rid` is set). `oldContentTypes` is
  // retained only for `notifyCellRecycled`, which is indexed by source position.
  const oldContentTypes = $cells.map(($cell) => $cell.contentType ?? null);
  // A promote re-key leaves bound cells carrying their row's OLD wire string;
  // map those back to the row they still paint so the reconcile keeps them
  // (and the focused editor inside) instead of replacing them.
  const oldRowIds = reconcileRowKeys(
    $cells.map(($cell) => ({
      rid: $cell.getAttribute("rid"),
      serial: $cell.getAttribute("rsid"),
    })),
    rows.map((row) => ({ wire: row.id.toString(), serial: row.id.serial })),
  );
  const targetRowIds = rows.map((row) => row.id.toString());
  const changesets = Changeset.make(oldRowIds, targetRowIds);
  const pluginManager = port.handle.pluginManager;
  const rowByKey = new Map(
    rows.map((row) => [row.id.toString(), row] as const),
  );

  // ── PHASE ORDER (unconditional) ─────────────────────────────────────
  // New order: inputs → PLANS → BINDINGS → scene build+diff → compile (both) →
  // applyStructure → op-list → applyCells → prevScene commit → updatePreviousState.
  //
  // Plans are computed BEFORE any structure mutation. Previously they ran AFTER
  // `markNew` mutated `previousRenderState` (descriptorType:"", isNew:true); that
  // mutation only influences `plan.updateType`/`plan.diff` (via
  // `RenderPlan.resolveUpdateType`), and those two fields have ZERO consumers on
  // the DOM bind path (`applyPlanFullRebind` never reads them, the scene build/
  // diff compares only descriptorType/skipContent/mode/timeState/stacking/
  // spanMode, and the sole `updateType` consumers are the CLI's own plan pass).
  // Verified by enumeration — so the reorder is a dead-signal move for the DOM.
  const context = port.createPlanContext(sheet, defaultColumn, widthPx, now);
  const plans = RenderPlan.makeForRows(
    context,
    rows,
    port.previousRenderState,
    "full",
    port.subcolumnCache,
  );

  // Single binding resolution per row (down from 2: the dark scene capture used
  // to re-resolve). Fed to BOTH the scene build and `applyPlanFullRebind`. The
  // non-throwing resolver returns null for a transiently un-cell-bindable alias
  // (height-only that pass).
  const bindings = rows.map((row) =>
    DefaultBinderHelpers.resolveCellBindingOrSkip(row, pluginManager) ?? null,
  );


  return { oldContentTypes, oldRowIds, targetRowIds, changesets, rowByKey, plans, bindings };
}

/**
 * The commit op-list breadcrumb (diagnostics only), off the same changeset and
 * plans the apply consumes.
 */
export function logCommitOps(port: ColumnBindPort, input: ColumnPassInput, r: FullPassReconciled): void {
  const { defaultColumn } = input;
  const { changesets, oldRowIds, targetRowIds, plans } = r;
  // Commit op-list, diagnostics-only. Assemble the
  // DomActionLog-equivalent op list from the SAME changeset + hoisted plans the
  // apply loop below consumes — off the plan inputs, before any DOM write — so
  // the log names exactly what this per-column commit will insert / recycle /
  // remove. Observe only: the apply loop stays byte-identical. The assembled
  // list feeds NOTHING but the breadcrumb below, so the whole O(n*m) pass is
  // gated on the breadcrumb actually being emitted.
  if (port.renderFlags().diagnostics()) {
    const commitOps = assembleCommitOps(
      changesets,
      oldRowIds,
      targetRowIds.map((key, i) => ({ key, hasLayout: plans[i]?.layout != null })),
    );
    if (commitOps.length > 0) {
      Tracing.log("commit/op-list", {
        column: defaultColumn.offset,
        ...summarizeCommitOps(commitOps),
      });
    }
  }

}
